import { useState, FormEvent, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "motion/react";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, ApiError, type OAuthProviderId } from "@/services/api";
import {
  listenForOAuthPopupResult,
  openOAuthPopup,
  watchOAuthPopupClosed,
} from "@/utils/oauthPopup";
import {
  recordOAuthProviderLogin,
  shouldAttemptSilentFeedforwardLogin,
} from "@/utils/oauthSession";

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

interface AuthInputProps {
  id: string;
  type: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  icon: React.ReactNode;
  disabled?: boolean;
  error?: string;
  minLength?: number;
}

const PROVIDER_LABELS: Record<OAuthProviderId, string> = {
  discord: "Discord",
  feedforward: "Feedforward",
};

function AuthInput({
  id,
  type,
  placeholder,
  value,
  onChange,
  icon,
  disabled,
  error,
  minLength,
}: AuthInputProps) {
  return (
    <div className="space-y-1">
      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#D4893E] transition-colors">
          {icon}
        </div>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          disabled={disabled}
          minLength={minLength}
          className={`w-full pl-12 pr-4 py-4 bg-white/5 border ${
            error ? "border-red-500/50" : "border-white/10"
          } rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4893E]/50 focus:bg-white/10 transition-all duration-300 disabled:opacity-50`}
        />
      </div>
      {error && (
        <p className="text-red-400 text-xs pl-4">{error}</p>
      )}
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProviderId | null>(null);
  const [discordConfigured, setDiscordConfigured] = useState(false);
  const [feedforwardConfigured, setFeedforwardConfigured] = useState(false);
  const silentAttemptedRef = useRef(false);

  const oauthConfigured = discordConfigured || feedforwardConfigured;

  useEffect(() => {
    const checkProviders = async () => {
      const [discordResult, feedforwardResult] = await Promise.allSettled([
        api.getOAuthAuthUrl("discord"),
        api.getOAuthAuthUrl("feedforward"),
      ]);

      setDiscordConfigured(
        discordResult.status === "fulfilled" && discordResult.value.configured,
      );
      setFeedforwardConfigured(
        feedforwardResult.status === "fulfilled" &&
          feedforwardResult.value.configured,
      );
    };
    checkProviders();
  }, []);

  const startOAuthPopup = useCallback(
    async (
      provider: OAuthProviderId,
      options: { silent?: boolean } = {},
    ) => {
      if (!options.silent) {
        setError(null);
      }
      setOauthLoading(provider);

      try {
        const response = await api.getOAuthAuthUrl(provider, {
          silent: options.silent,
        });
        if (!response.configured || !response.url) {
          if (!options.silent) {
            setError(`${PROVIDER_LABELS[provider]} login is not configured`);
          }
          setOauthLoading(null);
          return;
        }

        const popup = openOAuthPopup(response.url, provider, {
          silent: options.silent,
        });
        if (!popup) {
          if (!options.silent) {
            setError(
              "Popup was blocked. Please allow popups for this site and try again.",
            );
          }
          setOauthLoading(null);
          return;
        }

        let finished = false;

        const stopListening = listenForOAuthPopupResult(async (message) => {
          if (message.provider !== provider || finished) return;
          finished = true;
          stopListening();
          stopWatching();

          if (message.status === "error") {
            if (message.silentFallback || options.silent) {
              popup.close();
              setOauthLoading(null);
              return;
            }
            setError(message.error);
            setOauthLoading(null);
            popup.close();
            return;
          }

          try {
            const authResponse = await api.loginWithOAuth(
              provider,
              message.code,
            );
            login(authResponse.access_token, authResponse.user);
            recordOAuthProviderLogin(provider);

            try {
              const workspacesResponse = await api.listWorkspaces();
              if (workspacesResponse.workspaces.length > 0) {
                localStorage.setItem(
                  "active_workspace_id",
                  workspacesResponse.workspaces[0].id,
                );
              }
            } catch (workspaceErr) {
              console.warn("Failed to fetch workspaces on login:", workspaceErr);
            }

            popup.close();
            navigate("/analysis");
          } catch (err) {
            if (!options.silent) {
              if (err instanceof ApiError) {
                setError(
                  err.message ||
                    `Failed to authenticate with ${PROVIDER_LABELS[provider]}`,
                );
              } else {
                setError("An unexpected error occurred");
              }
            }
            popup.close();
          } finally {
            setOauthLoading(null);
          }
        });

        const stopWatching = watchOAuthPopupClosed(popup, () => {
          if (!finished) {
            stopListening();
            setOauthLoading(null);
          }
        });
      } catch (err) {
        if (!options.silent) {
          if (err instanceof ApiError) {
            setError(
              err.message || `Failed to start ${PROVIDER_LABELS[provider]} login`,
            );
          } else {
            setError("An unexpected error occurred");
          }
        }
        setOauthLoading(null);
      }
    },
    [login, navigate],
  );

  useEffect(() => {
    if (!feedforwardConfigured || silentAttemptedRef.current) return;
    if (!shouldAttemptSilentFeedforwardLogin()) return;

    silentAttemptedRef.current = true;
    void startOAuthPopup("feedforward", { silent: true });
  }, [feedforwardConfigured, startOAuthPopup]);

  const handleOAuthLogin = (provider: OAuthProviderId) => {
    void startOAuthPopup(provider, { silent: false });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await api.login(email, password);
      login(response.access_token, response.user);

      try {
        const workspacesResponse = await api.listWorkspaces();
        if (workspacesResponse.workspaces.length > 0) {
          const firstWorkspace = workspacesResponse.workspaces[0];
          localStorage.setItem("active_workspace_id", firstWorkspace.id);
        }
      } catch (workspaceErr) {
        console.warn("Failed to fetch workspaces on login:", workspaceErr);
      }
      
      navigate("/analysis");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Invalid email or password");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#063F6B] flex items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/">
            <img
              src="/logo-light.png"
              alt="Feedforward AI"
              className="h-12 w-auto mx-auto mb-6 hover:scale-105 transition-transform"
            />
          </Link>
        </motion.div>

        {/* Card */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="relative bg-[#063F6B]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl shadow-black/20">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-semibold text-white mb-2">
                Sign In
              </h1>
              <p className="text-slate-400 text-sm">
                Enter your credentials to access your account
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <AuthInput
                id="email"
                type="email"
                placeholder="Email address"
                value={email}
                onChange={setEmail}
                icon={<Mail className="w-5 h-5" />}
                disabled={isLoading}
                error={error && error.toLowerCase().includes("email") ? error : undefined}
              />

              <AuthInput
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={setPassword}
                icon={<Lock className="w-5 h-5" />}
                disabled={isLoading}
                minLength={8}
                error={error && error.toLowerCase().includes("password") ? error : undefined}
              />

              <div className="flex justify-end -mt-2">
                <Link
                  to="/forgot-password"
                  className="text-xs text-slate-400 hover:text-[#D4893E] transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {error && !error.toLowerCase().includes("email") && !error.toLowerCase().includes("password") && (
                <motion.div
                  className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-[#D4893E] hover:bg-[#C07830] text-white font-semibold rounded-xl shadow-lg transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Sign up CTA */}
              <p className="text-center text-sm text-slate-400">
                Don't have an account yet?{" "}
                <Link
                  to="/register"
                  className="text-[#D4893E] hover:text-[#EAA059] font-medium transition-colors"
                >
                  Sign up here
                </Link>
              </p>
            </form>

            {/* Divider */}
            {oauthConfigured && (
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#063F6B] text-slate-400">
                    Or continue with
                  </span>
                </div>
              </div>
            )}

            {/* OAuth providers */}
            {oauthConfigured && (
              <div className="space-y-3">
                {feedforwardConfigured && (
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin("feedforward")}
                    disabled={isLoading || oauthLoading !== null}
                    className="w-full py-4 bg-[#D4893E] hover:bg-[#C07830] text-white font-semibold rounded-xl shadow-lg overflow-hidden cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
                  >
                    {oauthLoading === "feedforward" ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Connecting to Feedforward...
                      </>
                    ) : (
                      <>
                        <img
                          src="/logo-no-text.png"
                          alt=""
                          className="h-5 w-auto brightness-0 invert"
                          aria-hidden
                        />
                        Continue with Feedforward
                      </>
                    )}
                  </button>
                )}

                {discordConfigured && (
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin("discord")}
                    disabled={isLoading || oauthLoading !== null}
                    className="w-full py-4 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-xl shadow-lg overflow-hidden cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
                  >
                    {oauthLoading === "discord" ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Connecting to Discord...
                      </>
                    ) : (
                      <>
                        <DiscordIcon className="w-5 h-5" />
                        Continue with Discord
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

          </div>
        </motion.div>

        {/* Back to home link */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Link
            to="/"
            className="text-slate-500 hover:text-white text-sm transition-colors inline-flex items-center gap-1"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to home
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
